import { injectable, injectFromBase } from 'inversify'
import cloneDeep from 'lodash-es/cloneDeep'
import DomainBaseService from '@/services/domain/domainBaseService'
import { DomainSettings } from '@/decorators/domainDecorators'
import UndoRedoHistory from '@/data/models/UndoRedoHistory'
import type UndoRedoHistoryRepository from '@/data/repositories/undoRedoHistoryRepository'

@DomainSettings({
  model: UndoRedoHistory,
})
@injectable()
@injectFromBase()
export default class UndoRedoHistoryService<State = unknown> extends DomainBaseService<
  UndoRedoHistoryRepository<State>
> {
  public saveState(key: string, state: State) {
    const history = this.getDomainRepository().findEntityByIdOrNull(key)
    const states = history?.states ?? []
    const currentStateSequence = history?.currentStateSequence ?? 0

    const newStates = [...states.slice(0, currentStateSequence), cloneDeep(state)]
    const newCurrentStateSequence = newStates.length

    this.getDomainRepository().insertOrUpdateItem({
      id: key,
      currentStateSequence: newCurrentStateSequence,
      states: newStates,
    })
  }

  public undo(key: string): State | undefined {
    const dataToUndo = this.getDataToUndo(key)
    if (!dataToUndo) {
      return undefined
    }
    this.getDomainRepository().insertOrUpdateItem({
      id: key,
      currentStateSequence: dataToUndo.sequence,
    })
    return dataToUndo.state
  }

  public redo(key: string): State | undefined {
    const dataToRedo = this.getDataToRedo(key)
    if (!dataToRedo) {
      return undefined
    }
    this.getDomainRepository().insertOrUpdateItem({
      id: key,
      currentStateSequence: dataToRedo.sequence,
    })
    return dataToRedo.state
  }

  public moveToSequence(key: string, sequence: number) {
    const dataToMoveToSequence = this.getDataToMoveToSequence(key, sequence)
    if (!dataToMoveToSequence) {
      return undefined
    }
    this.getDomainRepository().insertOrUpdateItem({
      id: key,
      currentStateSequence: dataToMoveToSequence.sequence,
    })
    return dataToMoveToSequence.state
  }

  public canUndo(key: string) {
    return !!this.getDataToUndo(key)
  }

  public canRedo(key: string) {
    return !!this.getDataToRedo(key)
  }

  public clear(key: string) {
    this.getDomainRepository().clear(key)
  }

  public getCurrentStateSequence(key: string) {
    const history = this.getDomainRepository().findEntityByIdOrNull(key)
    if (!history) {
      return 0
    }
    return history.currentStateSequence
  }

  protected getDataToMoveToSequence(key: string, sequence: number) {
    const history = this.getDomainRepository().findEntityByIdOrNull(key)
    if (!history) {
      return undefined
    }
    const stateIndex = sequence - 1
    const state = history.states[stateIndex]
    if (!state) {
      return undefined
    }
    return {
      state,
      sequence,
    }
  }

  protected getDataToUndo(key: string) {
    const previousStateSequence = this.getCurrentStateSequence(key) - 1
    return this.getDataToMoveToSequence(key, previousStateSequence)
  }

  protected getDataToRedo(key: string) {
    const nextStateSequence = this.getCurrentStateSequence(key) + 1
    return this.getDataToMoveToSequence(key, nextStateSequence)
  }
}
